import { format } from 'date-fns';
import { FILE_CHUNK_SIZE, RESOURCE_TYPE } from '../constants/resource-constants';
import { camelCase } from "lodash";



export const richErrorObject = (e: any) => {
    const errorResponse = {
      success: false,
      error: {
        message: '',
        stack: '',
        additionalInfo: {} as any
      }
    };

    try {
      // Safely extract the error message
      if (e?.message) {
        errorResponse.error.message = e.message;
      } else {
        errorResponse.error.message = 'An unknown error occurred';
      }

      // Include the stack trace for debugging
      if (e?.stack) {
        errorResponse.error.stack = e.stack;
      }

      // Extract additional error properties, if any
      Object.keys(e).forEach(key => {
        if (key !== 'message' && key !== 'stack' && key !== 'name') {
          errorResponse.error.additionalInfo[key] = e[key];
        }
      });

      return errorResponse;
    }
    catch (e) {
      console.log("Error creating rich error object")
    }

  }

  export function pickProperties<T extends object>(obj: T, keys: (keyof T)[]): Partial<T> {
    const result: Partial<T> = {};
    keys.forEach((key) => {
      if (key in obj) {
        result[key] = obj[key];
      }
    });
    return result;
  }

  export function generateRandomString(length: number, characters = 'abcdefghijklmnopqrstuvwxyz0123456789') {
    let result = '';

    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
    }

    return result;
  }

  export function splitStringToWords(text: string) {
    return [text].flatMap(sentence =>
      sentence.split(/(\s+)/).map(word => word.includes("\n\n") ? "\n\n" : word.trim())
    )
    .filter(word => word !== "");  // Remove empty strings but retain meaningful pauses
  }

  export const formatDate = (date: Date | null | undefined): string | null => {
    return date ? format(date, 'yyyy-MM-dd') : null;
  };


  export const formatDateHourMinuteSecond = (date: Date | null | undefined): string | null => {
    return date ? format(date, 'yyyy-MM-dd-HH-MM-SS') : null;
  };

  export const formatAddress = (address: any) => {
    if (!address) {
      return '';
    }

    const parts = [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.subdivision,
      address.postalCode,
      address.country,
    ];

    const nonEmptyParts = parts.filter(part => part); // Filter out empty strings and null/undefined values

    if (nonEmptyParts.length === 0) {
      return ''; // Handle the case where all parts are empty
    }

    return nonEmptyParts.join(', '); // Join with commas and spaces, but only if there are parts to join
  };

  export const dataURLtoFile = (dataurl: any, filename: string) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename , { type: mime });
  }

  export const removeExtension = (name: string) => {
    return name.replace(/\.[^/.]+$/, "")
  }

  export const getFileParts = (file: File): number => {
    const parts = Math.ceil(file.size / FILE_CHUNK_SIZE);
    return parts;
  };

  export const getFileType = (fileName: string): RESOURCE_TYPE => {
    const fileExtension = getFileExtension(fileName);
    const audioExtensions = ['mp3', 'wav', 'ogg', 'aac'];
    const videoExtensions = ['webm', 'mp4', 'mov', 'mpeg', 'mpg', 'avi', 'flv'];
    const imageExtensions = ['jpg', 'jpeg', 'png', 'tiff', 'svg', 'gif'];

    if (audioExtensions.indexOf(fileExtension) > -1) {
      return RESOURCE_TYPE.AUDIO;
    }

    if (imageExtensions.indexOf(fileExtension) > -1) {
      return RESOURCE_TYPE.IMAGE;
    }

    if (videoExtensions.indexOf(fileExtension) > -1) {
      return RESOURCE_TYPE.VIDEO;
    }

    return RESOURCE_TYPE.OTHER;
  };

  export const getFileExtension = (fileNameRaw: string): string => {
    const fileName = fileNameRaw.toLowerCase();
    const match = fileName.match(/[0-9a-z]+$/);
    if (!match || !match[0]) {
      console.log('Not a valid filename');
      throw new Error('Not a valid filename');
    }
    return match[0];
  };

  export const getFlexNameLabel = (user: any): string => {
    if (!user) return '';

    let displayName = user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : user.initials
            ? user.initials
            : '';

    if (user.email) {
        displayName += ` | ${user.email}`;
    }

    return displayName;
};

const isDateString = (value: any): boolean => {
  return typeof value === "string" && !isNaN(Date.parse(value));
};
// Helper function to convert object keys to camelCase recursively
// Helper function to convert object keys to camelCase and fix dates
export const toCamelCase = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(toCamelCase);
  } else if (obj !== null && typeof obj === "object") {
    return Object.entries(obj).reduce((acc: any, [key, value]) => {
      const newKey = camelCase(key); // Convert key to camelCase
      acc[newKey] = value; // Keep value unchanged
      return acc;
    }, {});
  }
  return obj;
};
